import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  CompanyProfile,
  RibbonsListing,
  RecentBatchesListing,
  ManufacturerDetailsModal,
  ManufacturerStrainsListing,
  RibbonsListingQueryParameters,
  ManufacturerBatchProductDetail,
  RecentBatchesListingQueryParameters,
  ManufacturerStrainsListingQueryParameters,
} from "./type";

const initialState: ManufacturerDetailsModal = {
  manufactuereRecentBatches: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  manufactureBatchesListingQueryParameters: {
    pageNo: 1,
    limit: 5,
    sortBy: 0,
    sortCriteria: "createdAt",
  },

  hasMoreBatches: false,

  manufacturerDetails: {
    _id: "",
    name: "",
    companyName: "",
    companyEmail: "",
    smartAccountAddress: "",
    companyPicture: "",
    profilePicture: "",
    customLinks: [],
    socialLinks: [],
    facebookProfile: "",
    instagramProfile: "",
    mobileNo: "",
    website: "",
    xProfileLink: "",
    state: "",
    walletUrl: "",
    brandName: "",
    bannerImage: "",
  },

  ribbonsListing: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  ribbonsListingQueryParameters: {
    pageNo: 1,
    limit: 10,
  },

  manufacturerStrainsListing: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },

  manufacturerStrainsListingQueryParameters: {
    pageNo: 1,
    limit: 10,
    sortBy: 0,
    sortCriteria: "batchCount",
  },
  hasMoreStrains: false,
  manufacturerBatchDetails: {
    _id: "",
    batchId: "",
    product: { type: "", subType: "", image: "" },
    manufacturerId: "",
    label: "",
    strain: {
      mongoId: "",
      name: "",
      strainId: "",
      lineage: [],
      strainType: "",
    },
    batchTokenId: "",
    coaTokenizedAt: "",
    growerNote: "",
    manufacturerName: "",
    sampleImage: "",
    coaPdfIpfsHash: "",
    sampleStatus: "",
    testedDate: "",
    coaNftViewUrl: "",
    blockchainTransaction: "",
    viewProductOnChainUrl: "",
    thcTotal: "",
    cbdTotal: "",
    climsId: "",
    tarpeneTotal: "",
    cannabinoidTotal: "",
    topThreeTerpenes: [],
    topThreeCannabinoids: [],
    manufacturerCompanyPicture: "",
    ratingReviews: [],
    consumerClaimId: "",
    isRatingReview: false,
    averageRating: 0,
    totalRating: 0,
    totalRatingPerson: 0,
    dataPlatform: "",
    maxThcMg: "",
    maxCbdMg: "",
    maxThcWeight: "",
    maxCbdWeight: "",
    totalTerpenesMg: "",
    totalCannabinoidsMg: "",
    totalCannabinoidsWeight: "",
    totalTerpenesPercentWeight: "",
    scanCount: "",
    isOtherBatchReviewAvailable: false,
  },
};

const manufacturerDetailsSlice = createSlice({
  name: "manufacturerDetails",
  initialState,
  reducers: {
    setManufacturerDetails: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<CompanyProfile>,
    ) => {
      state.manufacturerDetails = action.payload;
    },

    setManufacturerBatchDetails: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<ManufacturerBatchProductDetail>,
    ) => {
      state.manufacturerBatchDetails = action.payload;
    },

    setResetManufacturerDetails: (state: ManufacturerDetailsModal) => {
      state.manufacturerDetails = {
        _id: "",
        name: "",
        companyName: "",
        companyEmail: "",
        smartAccountAddress: "",
        companyPicture: "",
        profilePicture: "",
        customLinks: [],
        socialLinks: [],
        facebookProfile: "",
        instagramProfile: "",
        mobileNo: "",
        website: "",
        xProfileLink: "",
        state: "",
        walletUrl: "",
        brandName: "",
        bannerImage: "",
      };
      state.manufacturerBatchDetails = {
        _id: "",
        batchId: "",
        product: { type: "", subType: "", image: "" },
        manufacturerId: "",
        label: "",
        strain: {
          mongoId: "",
          name: "",
          strainId: "",
          lineage: [],
          strainType: "",
        },
        batchTokenId: "",
        coaTokenizedAt: "",
        growerNote: "",
        manufacturerName: "",
        sampleImage: "",
        coaPdfIpfsHash: "",
        sampleStatus: "",
        testedDate: "",
        coaNftViewUrl: "",
        blockchainTransaction: "",
        viewProductOnChainUrl: "",
        thcTotal: "",
        cbdTotal: "",
        climsId: "",
        tarpeneTotal: "",
        cannabinoidTotal: "",
        topThreeTerpenes: [],
        topThreeCannabinoids: [],
        manufacturerCompanyPicture: "",
        ratingReviews: [],
        consumerClaimId: "",
        isRatingReview: false,
        averageRating: 0,
        totalRating: 0,
        totalRatingPerson: 0,
        dataPlatform: "",
        maxThcMg: "",
        maxCbdMg: "",
        maxThcWeight: "",
        maxCbdWeight: "",
        totalTerpenesMg: "",
        totalCannabinoidsMg: "",
        totalCannabinoidsWeight: "",
        totalTerpenesPercentWeight: "",
        scanCount: "",
      };
    },

    setManufactureBatchesListing: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<RecentBatchesListing>,
    ) => {
      const newPayload: RecentBatchesListing = action.payload;

      state.manufactuereRecentBatches = {
        ...newPayload,
        data: [...state.manufactuereRecentBatches.data, ...newPayload.data],
      };
      state.hasMoreBatches = newPayload.totalPage > newPayload.pageNo;
    },

    setRibbonsListing: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<RibbonsListing>,
    ) => {
      const newPayload = action.payload;
      if (newPayload.pageNo > 1) {
        state.ribbonsListing = {
          ...newPayload,
          data: [...state.ribbonsListing.data, ...newPayload.data],
        };
      } else {
        state.ribbonsListing = newPayload;
      }
    },

    setRibbonsListingQueryParameters: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<RibbonsListingQueryParameters>,
    ) => {
      state.ribbonsListingQueryParameters = action.payload;
    },

    setResetRibbonsListing: (state: ManufacturerDetailsModal) => {
      state.ribbonsListing = {
        data: [],
        total: 0,
        limit: 0,
        pageNo: 0,
        nextHit: 0,
        totalPage: 0,
      };
      state.ribbonsListingQueryParameters = {
        pageNo: 1,
        limit: 10,
      };
    },

    setManufacturerBatchesListingQueryParameters: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<RecentBatchesListingQueryParameters>,
    ) => {
      state.manufactureBatchesListingQueryParameters = {
        ...action.payload,
      };
    },

    setResetManufacturerBatchesListing: (state: ManufacturerDetailsModal) => {
      state.manufactuereRecentBatches = {
        data: [],
        total: 0,
        limit: 10,
        pageNo: 1,
        nextHit: 0,
        totalPage: 0,
      };

      state.hasMoreBatches = false;

      state.manufactureBatchesListingQueryParameters = {
        pageNo: 1,
        limit: 10,
        sortBy: 0,
        sortCriteria: "createdAt",
      };
    },

    setRecentSortByCriteria: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<string | "1" | "2" | "3" | "4">,
    ) => {
      switch (action.payload) {
        case "1":
          state.manufactureBatchesListingQueryParameters = {
            ...state.manufactureBatchesListingQueryParameters,
            sortCriteria: "myCollection",
            sortBy: 0,
          };
          break;
        case "2":
          state.manufactureBatchesListingQueryParameters = {
            ...state.manufactureBatchesListingQueryParameters,
            sortCriteria: "othersCollection",
            sortBy: 0,
          };
          break;
        case "3":
          state.manufactureBatchesListingQueryParameters = {
            ...state.manufactureBatchesListingQueryParameters,
            sortCriteria: "createdAt",
            sortBy: 0,
          };
          break;
        case "4":
          state.manufactureBatchesListingQueryParameters = {
            ...state.manufactureBatchesListingQueryParameters,
            sortCriteria: "createdAt",
            sortBy: 1,
          };
          break;
        default:
          break;
      }
    },

    setManufacturerStrainsListing: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<ManufacturerStrainsListing>,
    ) => {
      const newPayload = action.payload;
      if (newPayload.pageNo > 1) {
        state.manufacturerStrainsListing = {
          ...newPayload,
          data: [...state.manufacturerStrainsListing.data, ...newPayload.data],
        };
      } else {
        state.manufacturerStrainsListing = newPayload;
      }
      state.hasMoreStrains = newPayload.totalPage > newPayload.pageNo;
    },

    setManufacturerStrainsListingQueryParameters: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<ManufacturerStrainsListingQueryParameters>,
    ) => {
      state.manufacturerStrainsListingQueryParameters = action.payload;
    },

    setResetManufacturerStrainsListing: (state: ManufacturerDetailsModal) => {
      state.manufacturerStrainsListing = {
        data: [],
        total: 0,
        limit: 0,
        pageNo: 0,
        nextHit: 0,
        totalPage: 0,
      };
      state.manufacturerStrainsListingQueryParameters = {
        pageNo: 1,
        limit: 10,
        sortBy: 0,
        sortCriteria: "batchCount",
      };
      state.hasMoreStrains = false;
    },

    setStrainSortByCriteria: (
      state: ManufacturerDetailsModal,
      action: PayloadAction<
        string | "A-Z" | "Z-A" | "Most to Least" | "Least to Most"
      >,
    ) => {
      switch (action.payload) {
        case "A-Z":
          state.manufacturerStrainsListingQueryParameters = {
            ...state.manufacturerStrainsListingQueryParameters,
            sortCriteria: "name",
            sortBy: 1,
          };
          break;
        case "Z-A":
          state.manufacturerStrainsListingQueryParameters = {
            ...state.manufacturerStrainsListingQueryParameters,
            sortCriteria: "name",
            sortBy: 0,
          };
          break;
        case "Most to Least":
          state.manufacturerStrainsListingQueryParameters = {
            ...state.manufacturerStrainsListingQueryParameters,
            sortCriteria: "batchCount",
            sortBy: 0,
          };
          break;
        case "Least to Most":
          state.manufacturerStrainsListingQueryParameters = {
            ...state.manufacturerStrainsListingQueryParameters,
            sortCriteria: "batchCount",
            sortBy: 1,
          };
          break;
        default:
          break;
      }
    },
  },
});

export const {
  setRibbonsListing,
  setManufacturerDetails,
  setRecentSortByCriteria,
  setResetManufacturerDetails,
  setManufactureBatchesListing,
  setResetManufacturerBatchesListing,
  setManufacturerBatchesListingQueryParameters,
  setRibbonsListingQueryParameters,
  setResetRibbonsListing,
  setStrainSortByCriteria,
  setManufacturerStrainsListing,
  setManufacturerStrainsListingQueryParameters,
  setResetManufacturerStrainsListing,
  setManufacturerBatchDetails,
} = manufacturerDetailsSlice.actions;
export default manufacturerDetailsSlice.reducer;
